import React, { useEffect, useState } from "react";
import {
  instituteMembers,
  instituteAddMember,
  instituteUpdateMember,
  instituteDeleteMember,
  instituteSetAdminSeat,
  instituteIncreaseSeats,
  instituteUpdateSettings,
  openInstituteBillingPortal,
  instituteApiKey,
  instituteGenerateApiKey,
  instituteRevokeApiKey,
} from "../api";
import { todayIso } from "../utils/age";

/**
 * Management panel for an institute admin (mirrors AdminSettingsPanel's modal
 * structure). Tabs: Members (roster + create/delete), Seats & Billing, Settings.
 *
 * `institute` is the currentUser.institute block from /me (admin variant, with
 * seats/billing detail); `onInstituteChange` lets the parent refresh /me after
 * changes that alter it (seat toggle, settings).
 */
export default function InstituteAdminPanel({ institute, onClose, onInstituteChange }) {
  const [activeTab, setActiveTab] = useState("members");

  const TABS = [
    { id: "members", label: "Members" },
    { id: "seats", label: "Seats & Billing" },
    { id: "settings", label: "Settings" },
    { id: "api", label: "Developer API" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4" onClick={onClose}>
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <svg className="w-5 h-5 text-dt-gold-bright shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            </svg>
            <h2 className="text-lg font-semibold text-slate-900 truncate">{institute?.name || "Institute"}</h2>
            <span className="ml-1 text-xs font-medium px-1.5 py-0.5 rounded bg-[var(--gold-bg)] text-dt-gold-bright border border-[var(--panel-line)] shrink-0">Institute</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 rounded-full hover:bg-slate-100 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6 shrink-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? "border-[var(--gold)] text-dt-gold-bright"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "members" && <MembersTab />}
          {activeTab === "seats" && (
            <SeatsTab institute={institute} onInstituteChange={onInstituteChange} />
          )}
          {activeTab === "settings" && (
            <SettingsTab institute={institute} onInstituteChange={onInstituteChange} />
          )}
          {activeTab === "api" && <ApiTab />}
        </div>
      </div>
    </div>
  );
}

function MembersTab() {
  const [members, setMembers] = useState([]);
  const [seats, setSeats] = useState({ used: 0, purchased: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", gender: "", date_of_birth: "",
    role: "student", temp_password: "",
  });
  const [creating, setCreating] = useState(false);
  // Shown once after creation so the admin can hand the credentials over.
  const [createdCreds, setCreatedCreds] = useState(null); // { email, password }
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    instituteMembers()
      .then((d) => {
        setMembers(d.members || []);
        setSeats({ used: d.seats_used, purchased: d.seats_purchased });
      })
      .catch((e) => setError(e.message || "Failed to load members"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const randomPassword = () => {
    const alphabet = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let out = "";
    const rand = new Uint32Array(10);
    window.crypto.getRandomValues(rand);
    for (const n of rand) out += alphabet[n % alphabet.length];
    return out;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.gender) { setError("Please select the member's gender."); return; }
    setCreating(true);
    try {
      const { member } = await instituteAddMember({
        ...form,
        email: form.email.trim().toLowerCase(),
        date_of_birth: form.date_of_birth || undefined,
      });
      setMembers((m) => [...m, member]);
      setSeats((s) => ({ ...s, used: s.used + 1 }));
      setCreatedCreds({ email: member.email, password: form.temp_password });
      setForm({ email: "", first_name: "", last_name: "", gender: "", date_of_birth: "", role: "student", temp_password: "" });
      setShowAdd(false);
    } catch (err) {
      setError(err.message || "Could not create the account.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(
      `Delete ${m.display_name}'s account? Their personal topics and progress will be permanently deleted. Topics they shared with the institute are kept.`
    )) return;
    setError(null);
    setBusyId(m.id);
    try {
      await instituteDeleteMember(m.id);
      setMembers((list) => list.filter((x) => x.id !== m.id));
      setSeats((s) => ({ ...s, used: Math.max(0, s.used - 1) }));
    } catch (err) {
      setError(err.message || "Could not delete the member.");
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPassword = async (m) => {
    const pw = randomPassword();
    if (!window.confirm(`Reset ${m.display_name}'s password? They'll be logged out and must log in with the new temporary password.`)) return;
    setError(null);
    setBusyId(m.id);
    try {
      await instituteUpdateMember(m.id, { reset_password: pw });
      setCreatedCreds({ email: m.email, password: pw });
    } catch (err) {
      setError(err.message || "Could not reset the password.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRoleToggle = async (m) => {
    const role = m.role === "student" ? "teacher" : "student";
    setError(null);
    setBusyId(m.id);
    try {
      const { member } = await instituteUpdateMember(m.id, { role });
      setMembers((list) => list.map((x) => (x.id === m.id ? member : x)));
    } catch (err) {
      setError(err.message || "Could not change the role.");
    } finally {
      setBusyId(null);
    }
  };

  const seatsLeft = Math.max(0, seats.purchased - seats.used);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Seats: <span className="font-semibold">{seats.used}/{seats.purchased}</span>
          <span className="text-slate-400"> · {seatsLeft} free</span>
        </p>
        <button
          onClick={() => { setShowAdd((v) => !v); setForm((f) => ({ ...f, temp_password: f.temp_password || randomPassword() })); }}
          disabled={!showAdd && seatsLeft === 0}
          className="px-3 py-1.5 rounded-lg bg-dt-gold-bright text-dt-ink text-xs font-semibold hover:brightness-105 disabled:opacity-50"
        >
          {showAdd ? "Cancel" : "Add member"}
        </button>
      </div>
      {seatsLeft === 0 && !showAdd && (
        <p className="text-xs text-amber-600">All seats are in use. Increase your seats to add more members.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* One-time credentials banner */}
      {createdCreds && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <p className="font-semibold mb-1">Share these credentials — shown only once</p>
          <p className="font-mono text-xs">{createdCreds.email}</p>
          <p className="font-mono text-xs">Temporary password: {createdCreds.password}</p>
          <p className="text-xs mt-1 text-emerald-700">They'll be asked to choose their own password at first login.</p>
          <button onClick={() => setCreatedCreds(null)} className="mt-2 text-xs font-semibold underline underline-offset-2">Dismiss</button>
        </div>
      )}

      {/* Add member form */}
      {showAdd && (
        <form onSubmit={handleCreate} className="rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text" required placeholder="First name" value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            />
            <input
              type="text" placeholder="Last name" value={form.last_name}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            />
          </div>
          <input
            type="email" required placeholder="Email address" value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
          />
          <div className="grid grid-cols-2 gap-3">
            <select
              value={form.gender}
              onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            >
              <option value="">Gender…</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            >
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Date of birth (optional — needed for 18+ features)</label>
            <input
              type="date" max={todayIso()} value={form.date_of_birth}
              onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Temporary password (they change it at first login)</label>
            <div className="flex gap-2">
              <input
                type="text" required minLength={8} value={form.temp_password}
                onChange={(e) => setForm((f) => ({ ...f, temp_password: e.target.value }))}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              />
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, temp_password: randomPassword() }))}
                className="px-3 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
              >
                Regenerate
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={creating}
            className="w-full py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 disabled:opacity-50"
          >
            {creating ? "Creating…" : `Create ${form.role} account`}
          </button>
        </form>
      )}

      {/* Roster */}
      {loading && <p className="text-sm text-slate-500">Loading members…</p>}
      {!loading && members.length === 0 && !showAdd && (
        <p className="text-sm text-slate-500">No members yet — add your first student or teacher.</p>
      )}
      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.id} className="rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-800 text-sm truncate">{m.display_name}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                  m.role === "teacher" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"
                }`}>
                  {m.role}
                </span>
                {m.force_password_change && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">temp password</span>
                )}
              </div>
              <p className="text-xs text-slate-400 truncate">{m.email}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={() => handleRoleToggle(m)}
                disabled={busyId === m.id}
                title={m.role === "student" ? "Make teacher" : "Make student"}
                className="px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                {m.role === "student" ? "→ Teacher" : "→ Student"}
              </button>
              <button
                onClick={() => handleResetPassword(m)}
                disabled={busyId === m.id}
                className="px-2 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Reset password
              </button>
              <button
                onClick={() => handleDelete(m)}
                disabled={busyId === m.id}
                className="px-2 py-1 rounded-lg border border-rose-200 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeatsTab({ institute, onInstituteChange }) {
  const [seats, setSeats] = useState({
    used: institute?.seats_used ?? 0,
    purchased: institute?.seats_purchased ?? 0,
  });
  const [adminTakesSeat, setAdminTakesSeat] = useState(!!institute?.admin_takes_seat);
  const [newSeats, setNewSeats] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  // Keep the display fresh (covers webhook-side seat syncs).
  useEffect(() => {
    instituteMembers()
      .then((d) => setSeats({ used: d.seats_used, purchased: d.seats_purchased }))
      .catch(() => {});
  }, []);

  const handleIncrease = async (e) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    const n = parseInt(newSeats, 10);
    if (!n || n <= seats.purchased) {
      setError(`Enter a number above your current ${seats.purchased} seats.`);
      return;
    }
    if (!window.confirm(
      `Increase to ${n} seats? The prorated cost of the ${n - seats.purchased} extra seat(s) for the rest of your year is charged to your card immediately.`
    )) return;
    setBusy(true);
    try {
      const d = await instituteIncreaseSeats(n);
      setSeats({ used: d.seats_used, purchased: d.seats_purchased });
      setMsg(`You now have ${d.seats_purchased} seats.`);
      setNewSeats("");
      onInstituteChange?.();
    } catch (err) {
      setError(err.message || "Could not update your seats.");
    } finally {
      setBusy(false);
    }
  };

  const handleAdminSeat = async (checked) => {
    setError(null);
    setBusy(true);
    try {
      const d = await instituteSetAdminSeat(checked);
      setAdminTakesSeat(d.admin_takes_seat);
      setSeats({ used: d.seats_used, purchased: d.seats_purchased });
      onInstituteChange?.();
    } catch (err) {
      setError(err.message || "Could not update your seat.");
    } finally {
      setBusy(false);
    }
  };

  const handlePortal = async () => {
    setError(null);
    try {
      const { url } = await openInstituteBillingPortal();
      window.location.assign(url);
    } catch (err) {
      setError(err.message || "Could not open the billing portal.");
    }
  };

  return (
    <div className="space-y-5">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {msg && <p className="text-sm text-emerald-600">{msg}</p>}

      <div className="rounded-xl border border-slate-200 p-4">
        <p className="text-sm text-slate-600 mb-2">
          Seats in use: <span className="font-semibold">{seats.used}/{seats.purchased}</span>
        </p>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-dt-gold-bright transition-all"
            style={{ width: `${seats.purchased ? Math.min(100, (seats.used / seats.purchased) * 100) : 0}%` }}
          />
        </div>
        {institute?.current_period_end && (
          <p className="text-xs text-slate-400 mt-2">
            {institute.cancel_at_period_end ? "Ends" : "Renews"}{" "}
            {new Date(institute.current_period_end).toLocaleDateString()}
          </p>
        )}
      </div>

      <form onSubmit={handleIncrease} className="rounded-xl border border-slate-200 p-4 space-y-2">
        <label className="block text-sm font-medium text-slate-700">Increase seats</label>
        <p className="text-xs text-slate-400">
          Extra seats are charged immediately at a prorated price for the rest of your year,
          then renew with the rest of your subscription.
        </p>
        <div className="flex gap-2">
          <input
            type="number" min={seats.purchased + 1} value={newSeats}
            onChange={(e) => setNewSeats(e.target.value)}
            placeholder={`More than ${seats.purchased}`}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
          />
          <button
            type="submit" disabled={busy || !newSeats}
            className="px-4 py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 disabled:opacity-50"
          >
            {busy ? "Updating…" : "Buy seats"}
          </button>
        </div>
      </form>

      <label className="flex items-start justify-between cursor-pointer gap-3 rounded-xl border border-slate-200 p-4">
        <span className="text-sm text-slate-600">
          Use a seat myself
          <span className="block text-xs text-slate-400">Take one of your seats to access the learning features with this account.</span>
        </span>
        <input
          type="checkbox" checked={adminTakesSeat} disabled={busy}
          onChange={(e) => handleAdminSeat(e.target.checked)}
          className="w-4 h-4 mt-0.5 shrink-0"
        />
      </label>

      <button
        onClick={handlePortal}
        className="w-full py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Manage billing (invoices & card)
      </button>
    </div>
  );
}

function SettingsTab({ institute, onInstituteChange }) {
  const [name, setName] = useState(institute?.name || "");
  const [hideGlobal, setHideGlobal] = useState(!!institute?.hide_global_topics);
  const [hideGroupShared, setHideGroupShared] = useState(!!institute?.hide_group_shared_topics);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setMsg(null);
    setSaving(true);
    try {
      await instituteUpdateSettings({
        name: name.trim(),
        hide_global_topics: hideGlobal,
        hide_group_shared_topics: hideGroupShared,
      });
      setMsg("Settings saved!");
      onInstituteChange?.();
    } catch (err) {
      setError(err.message || "Could not save your settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-5">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {msg && <p className="text-sm text-emerald-600">{msg}</p>}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Institute name</label>
        <input
          type="text" required value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
        />
      </div>

      <label className="flex items-start justify-between cursor-pointer gap-3">
        <span className="text-sm text-slate-600">
          Hide the default topics from our members
          <span className="block text-xs text-slate-400">
            Your students and teachers won't see the app's built-in curriculum or other
            users' published topics. Topics shared within your institute, their own
            personal topics, and any topic categories shared with you by Murājaʿa stay
            visible.
          </span>
        </span>
        <input
          type="checkbox" checked={hideGlobal}
          onChange={(e) => setHideGlobal(e.target.checked)}
          className="w-4 h-4 mt-0.5 shrink-0"
        />
      </label>

      <label className="flex items-start justify-between cursor-pointer gap-3">
        <span className="text-sm text-slate-600">
          Hide topic categories shared with us
          <span className="block text-xs text-slate-400">
            Murājaʿa can share restricted topic categories with your institute (or with
            individual members). Turn this on and your students and teachers won't see
            those categories or the topics inside them. Their own personal topics stay
            visible.
          </span>
        </span>
        <input
          type="checkbox" checked={hideGroupShared}
          onChange={(e) => setHideGroupShared(e.target.checked)}
          className="w-4 h-4 mt-0.5 shrink-0"
        />
      </label>

      <button
        type="submit" disabled={saving}
        className="w-full py-2.5 rounded-xl bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save settings"}
      </button>
    </form>
  );
}

function ApiTab() {
  const [keyInfo, setKeyInfo] = useState(null); // { has_key, prefix, created_at }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The raw key, shown exactly once right after generation.
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    instituteApiKey()
      .then(setKeyInfo)
      .catch((e) => setError(e.message || "Could not load your API key status."))
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = async () => {
    if (
      keyInfo?.has_key &&
      !window.confirm(
        "Generate a new key? Your current key stops working immediately — any integration using it must be updated."
      )
    ) return;
    setError(null);
    setBusy(true);
    try {
      const d = await instituteGenerateApiKey();
      setNewKey(d.api_key);
      setCopied(false);
      setKeyInfo({ has_key: true, prefix: d.prefix, created_at: d.created_at });
    } catch (err) {
      setError(err.message || "Could not generate an API key.");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke the API key? Any integration using it stops working immediately.")) return;
    setError(null);
    setBusy(true);
    try {
      await instituteRevokeApiKey();
      setKeyInfo({ has_key: false, prefix: "", created_at: null });
      setNewKey(null);
    } catch (err) {
      setError(err.message || "Could not revoke the API key.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
    } catch {
      /* clipboard unavailable — the key is still visible to copy by hand */
    }
  };

  const endpointBase = `${window.location.origin}/api/v1/institute`;

  return (
    <div className="space-y-5">
      {error && <p className="text-sm text-red-600">{error}</p>}

      <p className="text-sm text-slate-600">
        Automate account creation from your own platform: your developers can create,
        list, update and delete student/teacher accounts over a REST API.
      </p>

      {/* Key status / actions */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : keyInfo?.has_key ? (
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-700">
                API key: <span className="font-mono">{keyInfo.prefix}…</span>
              </p>
              {keyInfo.created_at && (
                <p className="text-xs text-slate-400">
                  Created {new Date(keyInfo.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={handleGenerate} disabled={busy}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Rotate
              </button>
              <button
                onClick={handleRevoke} disabled={busy}
                className="px-3 py-1.5 rounded-lg border border-rose-200 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-600">No API key yet.</p>
            <button
              onClick={handleGenerate} disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-dt-gold-bright text-dt-ink text-xs font-semibold hover:brightness-105 disabled:opacity-50"
            >
              {busy ? "Generating…" : "Generate API key"}
            </button>
          </div>
        )}

        {/* One-time key reveal */}
        {newKey && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <p className="font-semibold mb-1">Copy your API key — shown only once</p>
            <div className="flex items-center gap-2">
              <code className="font-mono text-xs break-all flex-1">{newKey}</code>
              <button
                onClick={handleCopy}
                className="px-2 py-1 rounded-lg border border-emerald-300 text-xs font-semibold shrink-0 hover:bg-emerald-100"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="text-xs mt-1 text-emerald-700">
              Store it securely — we only keep a hash, so it can't be shown again.
            </p>
            <button onClick={() => setNewKey(null)} className="mt-2 text-xs font-semibold underline underline-offset-2">
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Quick reference */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-2">
        <p className="text-sm font-medium text-slate-700">Quick reference</p>
        <p className="text-xs text-slate-500">
          Authenticate every request with your key:
        </p>
        <pre className="text-[11px] font-mono bg-slate-50 border border-slate-100 rounded-lg p-2 overflow-x-auto">
{`Authorization: Bearer <your-api-key>`}
        </pre>
        <ul className="text-xs text-slate-500 space-y-1 font-mono">
          <li>GET    {endpointBase}/members/</li>
          <li>POST   {endpointBase}/members/</li>
          <li>GET    {endpointBase}/members/&lt;id&gt;/</li>
          <li>PATCH  {endpointBase}/members/&lt;id&gt;/</li>
          <li>DELETE {endpointBase}/members/&lt;id&gt;/</li>
        </ul>
        <p className="text-xs text-slate-500">
          POST takes <code className="font-mono">{"{ email, first_name, last_name?, gender, role, date_of_birth? }"}</code>{" "}
          and returns the new member plus a one-time <code className="font-mono">temporary_password</code> to
          hand to the student — they choose their own password at first login. PATCH accepts{" "}
          <code className="font-mono">{"{ role }"}</code> and/or <code className="font-mono">{"{ reset_password: true }"}</code>.
          A full account uses one seat; when all seats are in use, creation returns HTTP 409.
        </p>
      </div>
    </div>
  );
}
